import { httpGet, httpPost } from '@/api/http';
import { V2_DOMAIN_OPTIONS } from '@/constants/endpoint';
import { useMutation, useQueryClient, type UseQueryReturnType } from '@tanstack/vue-query';
import type { AxiosError } from 'axios';
import { useAuthenticatedQuery } from '@/utils/query';
import { useNotificationStore } from '@/stores/useNotificationStore';
import { i18n } from '@/plugins/i18n';
import { type ComputedRef } from 'vue';

export enum OptionKey {
  SHOW_CALL_HISTORY = 'SHOW_CALL_HISTORY',
  SHOW_CURRENT_BREAK_REASON = 'SHOW_CURRENT_BREAK_REASON',
  ZERO_TOUCH_PROVISIONING = 'ZERO_TOUCH_PROVISIONING',
  AGENT_METRICS = 'AGENT_METRICS',
  ALLOW_EAVESDROP = 'ALLOW_EAVESDROP',
  WRAP_UP_AUTOMATIC = 'WRAP_UP_AUTOMATIC',
  WRAP_UP_TIMER_SECONDS = 'WRAP_UP_TIMER_SECONDS',
  ANONYMIZE_AGENTS = 'ANONYMIZE_AGENTS',
  SHOW_USER_STATUS = 'SHOW_USER_STATUS',
}

export type ToggleableOptionKey = Exclude<OptionKey, OptionKey.WRAP_UP_TIMER_SECONDS>;

interface DomainOptionsDTO {
  showCurrentBreakReason: boolean;
  showCallHistory: boolean;
  isZeroTouchProvisioningEnabled: boolean;
  showAgentMetrics: boolean;
  allowEavesdrop: boolean;
  wrapUpAutomatic: boolean;
  anonymizeAgents: boolean;
  wrapUpTimerSeconds: number;
  showUserStatus: boolean;
}

export const DOMAIN_OPTIONS_QUERY_KEY = 'domainOptions';

export const OPTION_KEY_MAP = {
  [OptionKey.SHOW_CALL_HISTORY]: 'showCallHistory',
  [OptionKey.SHOW_CURRENT_BREAK_REASON]: 'showCurrentBreakReason',
  [OptionKey.ZERO_TOUCH_PROVISIONING]: 'isZeroTouchProvisioningEnabled',
  [OptionKey.AGENT_METRICS]: 'showAgentMetrics',
  [OptionKey.ALLOW_EAVESDROP]: 'allowEavesdrop',
  [OptionKey.WRAP_UP_AUTOMATIC]: 'wrapUpAutomatic',
  [OptionKey.ANONYMIZE_AGENTS]: 'anonymizeAgents',
  [OptionKey.SHOW_USER_STATUS]: 'showUserStatus',
} as const;

export const getOptionProperty = (key: ToggleableOptionKey) => OPTION_KEY_MAP[key];

const domainOptionsQuery = {
  queryKey: [DOMAIN_OPTIONS_QUERY_KEY],
  queryFn: () => httpGet<DomainOptionsDTO>(V2_DOMAIN_OPTIONS),
  staleTime: Infinity,
};

// need to explicitly type due to issue
// https://github.com/TanStack/query/issues/6318
export const useDomainOptions = (): UseQueryReturnType<DomainOptionsDTO, AxiosError> => {
  return useAuthenticatedQuery(domainOptionsQuery);
};

const getOptionMessages = (option: OptionKey) => {
  switch (option) {
    case OptionKey.ZERO_TOUCH_PROVISIONING:
      return {
        enabled: 'provisioning-options.enabled-success',
        disabled: 'provisioning-options.disabled-success',
        error: 'provisioning-options.modification-error',
      };
    default:
      return {
        enabled: 'domain-settings.option-enabled',
        disabled: 'domain-settings.option-disabled',
        error: 'domain-settings.option-error',
      };
  }
};

export const useToggleOption = (optionKey: OptionKey, optionValue: ComputedRef<boolean>) => {
  const { mutate: toggleOption, isPending } = useUpdateDomainOptionValue();
  const { data } = useDomainOptions();

  const onToggle = () => {
    if (optionValue.value == null) return;
    const newValue = !optionValue.value;
    toggleOption(
      { value: newValue.toString(), optionKey },
      {
        onSuccess: () => {
          // Added timeout to give short delay before next toggle update which caused error notification
          setTimeout(() => {
            const shouldDisableEavesdrop = optionKey === OptionKey.ANONYMIZE_AGENTS && newValue && data.value?.allowEavesdrop;
            if (shouldDisableEavesdrop) toggleOption({ optionKey: OptionKey.ALLOW_EAVESDROP, value: 'false' });
          }, 500);
        },
      }
    );
  };

  return { isPending, onToggle };
};

export const useUpdateDomainOptionValue = () => {
  const queryClient = useQueryClient();
  const { setSnackBar } = useNotificationStore();
  const { t } = i18n.global;

  return useMutation({
    mutationFn: ({ optionKey, value }: { optionKey: OptionKey; value: string }) => httpPost(`${V2_DOMAIN_OPTIONS}/${optionKey}/value/${value}`),

    onSuccess: async (_, { value, optionKey }) => {
      await queryClient.invalidateQueries({ queryKey: [DOMAIN_OPTIONS_QUERY_KEY] });

      const messages = getOptionMessages(optionKey);
      setSnackBar({
        message: value !== 'false' ? t(messages.enabled) : t(messages.disabled),
        variant: 'success',
      });
    },

    onError: async (_, { optionKey }) => {
      const messages = getOptionMessages(optionKey);
      setSnackBar({
        message: t(messages.error),
        variant: 'error',
      });
    },
  });
};
